import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import CheckOutlinedIcon from '@mui/icons-material/CheckOutlined';
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import type { SelectChangeEvent } from '@mui/material';
import { styled } from '@mui/material';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import Input from '@mui/material/Input';
import InputAdornment from '@mui/material/InputAdornment';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import Skeleton from '@mui/material/Skeleton';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import type { ActionArgs, LoaderArgs, Session } from '@remix-run/node';
import { json, redirect } from '@remix-run/node';
import type { Fetcher, ShouldRevalidateFunction } from '@remix-run/react';
import { Outlet, useFetcher, useFetchers, useLoaderData, useMatches, useNavigation } from '@remix-run/react';
import _ from 'lodash';
import * as React from 'react';
import Image from 'remix-image';
import invariant from 'tiny-invariant';
import parser from 'ua-parser-js';
import CircularProgress from '~/components/shared/CircularProgress';
import ProductModal from '~/components/shared/ProductModal';
import { MuiRemixLink } from '~/components/shared/links';
import { Info } from '~/components/shared/tooltips';
import NoImageAvailable from '~/images/no_image_available.svg';
import type { ErrorResponse } from '~/services/api-utils.server';
import type {
  CustomPackTemplatePayload,
  ItemAttributes,
  PackBuilderPricesResponse,
  PackTemplate,
  PriceEntry,
  Product,
} from '~/services/api.server';
import { cartApi, scApi } from '~/services/api.server';
import { getUpdatedAuthentication } from '~/services/auth.server';
import { setNotification } from '~/services/cookies.server';
import { commitSession, getSession } from '~/services/session.server';
import { getEmptyArray, getFormattedPrice, isErrorResponse, isPackProduct, typedBoolean } from '~/utils/common';
import { ellipsisStyle, scrollbarStyle } from '~/utils/common-styles';
import { ConfigurationTypes, ProductTypes, baseImgUrl } from '~/utils/constants';
import { TYPES_PARAM } from '~/utils/searchParams';
import { RemoveUnsetSearchParams } from '~/utils/shop';
import { validCombination } from '~/utils/validCombination';
import type { TDesignLab } from '../design-lab';

type PackPrice = {
  pack_price: PriceEntry;
  products: { [key: string]: PriceEntry };
  container: PriceEntry;
  filler: PriceEntry;
};

export type Pack = {
  name: string;
  quantity: number;
  container: Product | null;
  filler: Product | null;
  products: Array<Product>;
  prices: PackPrice & { breakdowns: { [key: string]: PackPrice } } & {
    pack_prices: {
      blank: PriceEntry;
      decoration: PriceEntry;
      total: PriceEntry;
    };
  };
  items_attributes: Record<number, ItemAttributes>;
  items_units_per_pack: Record<number, number>;
  no_colors?: number;
  pack_template_id: number;
};

export const shouldRevalidate: ShouldRevalidateFunction = ({ formData }) => Boolean(formData?.has('_action'));

export const initialPrice = { amount: 0, display: '0.00' };
const breakdowns = {
  50: { pack_price: initialPrice, products: {}, container: initialPrice, filler: initialPrice },
  100: { pack_price: initialPrice, products: {}, container: initialPrice, filler: initialPrice },
  150: { pack_price: initialPrice, products: {}, container: initialPrice, filler: initialPrice },
  200: { pack_price: initialPrice, products: {}, container: initialPrice, filler: initialPrice },
};
export type Breakdowns = typeof breakdowns;
export const initialPackPrices = {
  pack_price: initialPrice,
  pack_prices: {
    blank: initialPrice,
    decoration: initialPrice,
    total: initialPrice,
  },
  container: initialPrice,
  filler: initialPrice,
  products: {},
  breakdowns,
};

export const initialPackConfig: Pack = {
  name: '',
  quantity: 100,
  container: null,
  filler: null,
  products: [],
  prices: initialPackPrices,
  items_attributes: {},
  items_units_per_pack: {},
  no_colors: 1,
  pack_template_id: -1,
};

export async function loader({ request }: LoaderArgs) {
  const session = await getSession(request);
  const pack = session.get('packBuilder') as Pack;
  if (!pack) {
    session.set('packBuilder', initialPackConfig);
    return redirect('/build-pack', await getCookieHeader(session));
  }

  const url = new URL(request.url);
  RemoveUnsetSearchParams(url);

  if (!url.searchParams.has(TYPES_PARAM)) {
    url.searchParams.append(TYPES_PARAM, ProductTypes.SUPPLIER_PRODUCT.toString());
  }

  const action = session.has('action') ? (session.get('action') as { name: string; value: number }) : null;

  if (action?.name !== packActions.changeName) {
    const missingPrices = Object.keys(pack.prices.breakdowns).some((k) =>
      Object.values(pack.prices.breakdowns[k].products).some((v) => v.amount === 0)
    );
    if (action?.name === packActions.changeQuantity && pack.prices.breakdowns[action.value] && !missingPrices) {
      const prices: Pack['prices'] = { ...pack.prices, ...pack.prices.breakdowns[action.value] };
      return jsonWithCookieHeader({ ...pack, prices }, session);
    }

    if (pack.products.length > 0 || pack.container || pack.filler) {
      const pricesRequest = getPricesRequestData(pack);
      const pricesResponse = await scApi.getPackBuilderPrices(pricesRequest);

      if (!isErrorResponse(pricesResponse)) {
        const updatedSession = await getSession(request);
        const updatedPack = updatedSession.get('packBuilder') as Pack;
        const prices = parsePrices(pricesResponse, updatedPack.prices);
        const products = [...updatedPack.products, updatedPack.container, updatedPack.filler].filter(typedBoolean);
        const withoutPrice = _.xor(
          products.map((p) => p.id),
          Object.keys(prices.products).map((k) => parseInt(k.toString(), 10))
        );
        withoutPrice.forEach((id) => {
          prices.products[id] = prices.products[id] ?? initialPrice;
          Object.keys(prices.breakdowns).forEach((k) => {
            prices.breakdowns[k].products[id] = prices.breakdowns[k].products[id] ?? initialPrice;
          });
        });
        pricesResponse.products.forEach((p) => {
          updatedPack.items_attributes[p.product] = {
            product_id: p.product,
            attributes: Object.entries(p.attributes).map(([key, value]) => ({ name: key as string, value })),
          };
        });

        if (updatedPack.container) {
          const updatedContainer = pricesResponse?.products?.find((p) => p.product === updatedPack.container?.id);
          updatedPack.container.image = updatedContainer?.image;
          updatedPack.container.blank_image = updatedContainer?.blank_image;
        }

        if (updatedPack.filler) {
          const updatedFiller = pricesResponse?.products?.find((p) => p.product === updatedPack.filler?.id);
          updatedPack.filler.image = updatedFiller?.image;
          updatedPack.filler.blank_image = updatedFiller?.blank_image;
        }

        updatedPack.products.forEach((p) => {
          const updatedProduct = pricesResponse?.products?.find(
            (priceResponseProduct) => priceResponseProduct.product === p?.id
          );

          p.image = updatedProduct?.image;
          p.blank_image = updatedProduct?.blank_image;
        });
        return jsonWithCookieHeader({ ...updatedPack, prices }, updatedSession);
      }
    } else if (pack.prices.pack_price.amount > 0) {
      return jsonWithCookieHeader({ ...pack, prices: initialPackPrices }, session);
    }
  }
  return jsonWithCookieHeader(pack, session);
}

export const packActions = {
  changeName: 'changeName',
  changeQuantity: 'changeQuantity',
  addProduct: 'addProduct',
  removeProduct: 'removeProduct',
  changeUnitsPerPack: 'changeUnitsPerPack',
  setContainer: 'setContainer',
  setFiller: 'setFiller',
  addToCart: 'addToCart',
  addDesignLab: 'addDesignLab',
  changePackDecorationColorQuantity: 'changePackDecorationColorQuantity',
  addPackTemplate: 'addPackTemplate',
  changeAttribute: 'changeAttribute',
  changeAttributes: 'changeAttributes',
  deleteCurrentPack: 'deleteCurrentPack',
  saveTemplate: 'saveTemplate',
} as const;
type PackAction = keyof typeof packActions;

export async function action({ request }: ActionArgs) {
  const { headers = new Headers(), user } = await getUpdatedAuthentication(request);
  const session = await getSession(request);
  const pack = session.get('packBuilder') as Pack;
  const form = await request.formData();
  const action = form.get('_action');
  const deviceType = parser(request.headers.get('user-agent') ?? undefined).device.type || 'desktop';

  if (action === packActions.changeName) {
    const name = form.get('name');
    session.flash('action', { name: packActions.changeName, value: name });
    session.set('packBuilder', { ...pack, name });
  } else if (action === packActions.changeQuantity) {
    const quantity = form.get('quantity');
    session.flash('action', { name: packActions.changeQuantity, value: quantity });
    session.set('packBuilder', { ...pack, quantity });
  } else if (action === packActions.addProduct) {
    const unitsPerPack = form.get('unitsPerPack')?.toString();
    const product = getProductFromFormData(form);
    const productInPack = product && pack.products.find((p) => p.id === product.id);
    const color = form.get('color')?.toString();
    const size = form.get('size')?.toString();

    if (!product || (!unitsPerPack && productInPack)) {
      return null;
    }
    if (unitsPerPack) {
      //  Add product with Modal
      if (productInPack) {
        //  Product already in pack
        if (pack.items_units_per_pack[product.id] === +unitsPerPack) {
          //  Same quantity
          const itemColorInPack = pack.items_attributes[product.id].attributes?.find(
            (attribute) => attribute.name === 'color'
          )?.value;

          if (color && itemColorInPack === color) {
            //  Same color => Item in pack is the same as the one selected
            return null;
          }
        }
      } else {
        //  Product is not in pack
        pack.products.push(product);
      }
      pack.items_units_per_pack[product.id] = +unitsPerPack;
    } else {
      //  Add product with radio button
      pack.products.push(product);
      pack.items_units_per_pack[product.id] = 1;
    }
    if (color) {
      //  value: color throws a warning if there is no "if (color)"
      if (size) {
        pack.items_attributes[product.id] = {
          product_id: product.id,
          attributes: [
            { name: 'color', value: color },
            { name: 'size', value: size },
          ],
        };
      } else {
        pack.items_attributes[product.id] = { product_id: product.id, attributes: [{ name: 'color', value: color }] };
      }
    }
    session.set('packBuilder', { ...pack, pack_template_id: -1 });
    headers.append('Set-Cookie', await commitSession(session));

    if (deviceType === 'mobile') {
      setNotification(session, 'success', 'The product has been added!', 'bottom-center');
    }
  } else if (action === packActions.changeAttribute) {
    const product = getProductFromFormData(form);
    const productInPack = product && pack.products.find((p) => p.id === product.id);
    const attribute = JSON.parse(form?.get('attribute')?.toString() || '');
    const color = form.get('color')?.toString();

    if (!product || !productInPack || (attribute.name === 'size' && !color)) {
      return null;
    }
    const attributes = [];
    if (attribute.name === 'book_type') {
      attributes.push(attribute);
    }
    if (attribute.name === 'size') {
      attributes.push(attribute, { name: 'color', value: color });
    }

    pack.items_attributes[product.id] = {
      product_id: product.id,
      attributes: attributes,
    };
    session.set('packBuilder', pack);
    headers.append('Set-Cookie', await commitSession(session));
  } else if (action === packActions.changeAttributes) {
    const productId = form?.get('productId')?.toString();
    const productInPack = productId && pack.products.find((p) => p.id === +productId);
    const attributes = JSON.parse(form?.get('attributes')?.toString() || '');

    if (!productId || !productInPack || !attributes) {
      return null;
    }

    pack.items_attributes[+productId] = { product_id: +productId, attributes };
    session.set('packBuilder', pack);
    headers.append('Set-Cookie', await commitSession(session));
  } else if (action === packActions.removeProduct) {
    const productId = form.get('productId');
    if (!productId) {
      return null;
    }

    pack.products = pack.products.filter((p) => p.id !== +productId);
    Object.values(pack.prices.breakdowns).forEach((breakdown) => {
      delete breakdown.products[+productId];
    });
    delete pack.prices.products[+productId];
    delete pack.items_attributes[+productId];
    delete pack.items_units_per_pack[+productId];

    session.set('packBuilder', { ...pack, pack_template_id: -1 });
    if (deviceType === 'mobile') {
      setNotification(session, 'success', 'The product has been removed!', 'bottom-center');
    }
  } else if (action === packActions.changeUnitsPerPack) {
    const productId = form.get('productId')?.toString();
    const unitsPerPack = form.get('unitsPerPack')?.toString();
    if (!productId || !unitsPerPack) {
      return null;
    }
    pack.items_units_per_pack[+productId] = +unitsPerPack;
    session.set('packBuilder', pack);
  } else if (action === packActions.setContainer || action === packActions.setFiller) {
    const key = action === packActions.setFiller ? 'filler' : 'container';
    const item = getProductFromFormData(form);
    //  If client wants to remove filler/container, item will be undefined
    const itemId = pack[key]?.id;
    const color = form.get('color')?.toString();
    if (color && item) {
      pack.items_attributes[item.id] = { product_id: item.id, attributes: [{ name: 'color', value: color }] };
    }
    if (!item && itemId) {
      //  Deleting item if there is filler/container
      delete pack.items_attributes[itemId];
    }
    session.set('packBuilder', { ...pack, [key]: item, pack_template_id: -1 });
  } else if (action === packActions.addDesignLab) {
    const { headers = new Headers() } = await getUpdatedAuthentication(request);
    const { container, filler } = pack;
    const images = [...pack.products, container, filler].map((p) => p?.image ?? p?.blank_image).filter(typedBoolean);
    const packImage = await scApi.getCollages({ products: images });
    const addToCart = Boolean(form.get('addToCart'));
    const packId = Number(form.get('packId'));

    const containerPrice = pack.prices.products[container!.id];
    const fillerPrice = filler ? pack.prices.products[filler.id] : initialPrice;

    const product = {
      image: packImage.collage_url,
      name: pack.name,
      container: { ...pack.container!, prices: { ...pack.container!.prices, price: containerPrice } },
      filler: filler ? { ...filler, prices: { ...filler.prices, price: fillerPrice } } : null,
      items: pack.products.map((p) => ({
        id: p.id,
        product: { ...p, prices: { ...p.prices, price: pack.prices.products[p.id] } },
        units_per_pack: pack.items_units_per_pack[p.id],
      })),
      prices: {
        price: pack.prices.pack_price,
        currency: 'USD',
        maximum: pack.prices.pack_price,
        minimum: pack.prices.pack_price,
      },
      available_attributes: {},
      can_be_container: false,
      can_be_filler: false,
      category: { id: -1, name: 'Draft' },
      design_status: { id: -1, name: 'Pending Design' },
      design_lab_ready: true,
      // Use random negative numbers to avoid conflicts with existing products
      id: packId || -1 * Math.floor(Math.random() * 1000),
      max_sample_quantity: 0,
      minimum: 0,
      production_time: 0,
      rating: '',
      requires_design: true,
      sample_price: { amount: 0, display: '0.00', currency: 'USD' },
      sellable: false,
      slug: 'custom-pack',
      type: { id: 3, name: 'Pack Template' },
      unavailable_variants: [],
    };

    if (addToCart) {
      cartApi.addProduct(
        session,
        Number(pack.quantity),
        product,
        Object.values(pack.items_attributes),
        ConfigurationTypes.decorated
      );
      setNotification(session, 'success', 'Your pack has been added to the cart successfully');
      headers.append('Set-Cookie', await commitSession(session));
    } else {
      const designLab: TDesignLab = {
        quantity: pack.quantity,
        product,
        items_attributes: Object.values(pack.items_attributes),
        mockup_images: {},
        items_units_per_pack: {} as Record<number, number>,
        last_page_comes_from: '/build-pack',
        from_pack_builder: true,
        items_no_colors: {},
      };

      session.set('designLab', designLab);
      headers.append('Set-Cookie', await commitSession(session));
      if (isPackProduct(designLab.product)) {
        throw redirect(`/design-lab/${designLab.product.slug}`, { headers });
      } else {
        throw redirect(`/design-lab/bulk/${designLab.product.slug}`, { headers });
      }
    }
  } else if (action === packActions.changePackDecorationColorQuantity) {
    const quantity = form.get('decorationQuantity');
    const updatedProducts: Array<Product> = pack.products.map((product) => ({ ...product, no_colors: quantity }));

    session.set('packBuilder', { ...pack, products: updatedProducts, no_colors: quantity });
  } else if (action === packActions.addPackTemplate) {
    const packTemplateId = form.get('packTemplateId')?.toString() as string;
    const { user } = await getUpdatedAuthentication(request);

    const accountId = session.get('accountId');
    const packTemplate = (await scApi.getProduct(user?.accessToken, accountId, packTemplateId)) as
      | PackTemplate
      | ErrorResponse;

    if (isErrorResponse(packTemplate)) {
      return null;
    } else {
      const colors = form?.get('itemsColor')
        ? (JSON.parse(form?.get('itemsColor')?.toString() ?? '') as Record<number, string>)
        : null;
      const packItems = packTemplate.items.map((item) => ({ ...item.product })) as Product[];
      // reset pack properties
      pack.items_units_per_pack = {};
      pack.items_attributes = {};
      packTemplate.items.forEach((item) => {
        pack.items_units_per_pack[Number(item.product?.id)] = item.units_per_pack;
        // add item selected color
        if (colors && item.product?.id && colors[item.product?.id]) {
          pack.items_attributes[Number(item.product?.id)] = {
            ...pack.items_attributes[Number(item.product?.id)],
            attributes: [{ name: 'color', value: colors[item.product?.id] }],
          };
        }
      });

      session.set('packBuilder', {
        ...pack,
        container: packTemplate.container,
        filler: packTemplate.filler,
        products: packItems,
        no_colors: 1,
        pack_template_id: Number(packTemplateId),
      });
    }
  } else if (action === packActions.deleteCurrentPack) {
    session.set('packBuilder', initialPackConfig);
  } else if (action === packActions.saveTemplate) {
    const packCollage = form.get('packCollage');
    invariant(packCollage, 'Pack collage image must be set');
    const accountId = session.get('accountId');
    const customPackTemplate: CustomPackTemplatePayload = {
      name: pack.name,
      image: packCollage.toString(),
      container_id: pack.container?.id!,
      ...(pack.filler?.id ? { filler_id: pack.filler.id } : {}),
      items: pack.products.map((product) => ({
        product_id: product.id,
        units_per_pack: pack.items_units_per_pack[product.id],
      })),
    };
    const customPackResponse = await scApi.createCustomPackTemplate(user?.accessToken!, accountId, customPackTemplate);
    if (isErrorResponse(customPackResponse)) {
      setNotification(session, 'error', 'There was an error while saving your designs');
    } else {
      setNotification(session, 'success', 'The designs have been added to “My products” in Dashboard', 'top-right', {
        label: 'Go to My Products',
        href: `/dashboard/my-products`,
      });
      session.set('packBuilder', initialPackConfig);
      throw redirect(`/shop/${customPackResponse.slug}`, await getCookieHeader(session));
    }
  }

  return json(null, await getCookieHeader(session));
}

export default function PackConfig() {
  const pack = useLoaderData<typeof loader>();

  const isSummary = useMatches().some((route) => route.pathname === '/build-pack/create/summary');
  if (isSummary) return <Outlet />;
  return (
    <Stack overflow="hidden">
      <PackPrices />
      <Box mt={2} px={2} pb={3} flex={1} sx={{ ...scrollbarStyle }}>
        <PackPackaging pack={pack as Pack} />
        <PackProducts pack={pack as Pack} />
      </Box>
    </Stack>
  );
}

function PackPrices() {
  const { prices, ...pack } = useLoaderData<typeof loader>();
  const [open, setOpen] = React.useState(false);
  const [contentHeight, setContentHeight] = React.useState(0);
  const data = useLoaderData<typeof loader>();
  const [quantity, setQuantity] = React.useState<number | string>(data.quantity);

  const [prevQuantity, setPrevQuantity] = React.useState(quantity);

  const isChangingQuantity = React.useRef(false);
  const isFetchingPrice = useIsFetchingPrices();

  const navigation = useNavigation();
  const isAddingToDesignLab = navigation.formData?.get('_action') === packActions.addDesignLab;

  const { submit } = useFetcher();
  const mountedRef = React.useRef(false);
  React.useEffect(() => {
    if (mountedRef.current && +quantity > 0 && quantity !== prevQuantity) {
      isChangingQuantity.current = true;
      const debounced = setTimeout(() => {
        isChangingQuantity.current = false;
        submit({ _action: 'changeQuantity', quantity: quantity.toString() }, { method: 'post' });
      }, 500);
      return () => clearTimeout(debounced);
    }
    mountedRef.current = true;
  }, [quantity, submit, prevQuantity]);

  React.useEffect(() => {
    if (!isFetchingPrice && !isChangingQuantity.current) {
      setPrevQuantity(quantity);
    }
  }, [quantity, isFetchingPrice]);

  const isPackComplete = pack.name && pack.container && pack.products.length > 0;
  const disableStartDesign = !isPackComplete || isFetchingPrice || isAddingToDesignLab;

  function handleNewQuantity(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) {
    const qty = e.target.value;
    const parsedQty = parseInt(qty, 10);
    if ((parsedQty === 0 || parsedQty > 5000) && qty !== '') return;
    setQuantity(qty ? parsedQty : '');
  }

  return (
    <Box px={2}>
      {/* Pack */}
      <Stack>
        {/* Pack name input */}
        <PackName />
        {/* Start designing pack button */}
        <Box>
          <Info title={!isPackComplete ? 'Make sure you have selected name, container and products for your pack' : ''}>
            <MuiRemixLink href="/build-pack/create/summary" style={{ textDecoration: 'none' }}>
              <Button
                variant="contained"
                sx={{ textTransform: 'uppercase' }}
                disabled={disableStartDesign}
                fullWidth
                disableElevation
              >
                View Summary {!isPackComplete && <InfoOutlinedIcon fontSize="small" sx={{ ml: 1.25 }} />}
              </Button>
            </MuiRemixLink>
          </Info>
        </Box>
        {/* Price breakdown button */}
        <Box
          display="flex"
          justifyContent="space-between"
          pl={1.25}
          pr={2}
          py={1.25}
          alignItems="center"
          mt={2}
          width="100%"
          borderRadius="8px 8px 0px 0px"
          bgcolor="#F0F5F9"
        >
          <Typography variant="caption" fontWeight="medium">
            Price Breakdown
          </Typography>

          <IconButton onClick={() => setOpen((prev) => !prev)} sx={{ p: 0 }}>
            <ExpandMoreIcon sx={{ transform: `rotate(${open ? '-180deg' : 0})`, transition: 'transform 350ms ease' }} />
          </IconButton>
        </Box>
        {/* Price Breakdown content when open*/}
        <Box
          sx={[
            {
              maxHeight: 0,
              backgroundColor: '#FAFAFA',
              transition: 'max-height 0.3s',
              overflow: 'hidden',
            },
            open && { maxHeight: contentHeight },
          ]}
        >
          <Box ref={(ref) => setContentHeight(62 ?? 0)} gap={1} pb={3} px={1} borderRadius="0px 0px 8px 8px">
            <Stack direction="row" justifyContent="space-around">
              {[50, 100, 150, 200].map((q) => (
                <Button
                  key={q}
                  onClick={() => setQuantity(q)}
                  sx={{
                    borderRadius: 2,
                    color: 'text.primary',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'space-around',
                    alignItems: 'center',
                    height: '62px',
                    py: 2,
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: 700,
                    }}
                    margin={0}
                  >
                    {q}
                  </Typography>
                  <Typography
                    variant="body2"
                    color="primary.light"
                    sx={[isFetchingPrice && { color: 'text.disabled' }]}
                  >
                    {getFormattedPrice(prices.breakdowns[q].pack_price.amount / 100)}
                  </Typography>
                </Button>
              ))}
            </Stack>
          </Box>
        </Box>
        {/* Price per pack and price */}
        <Box mt={2} px={1}>
          <Grid container justifyContent="space-between" alignItems="center">
            <Typography variant="body2">Price per pack</Typography>

            <Stack direction="row" alignItems="center">
              <CircularProgress show={isFetchingPrice} size={12} containerProps={{ mr: 1 }} />
              <Typography variant="body2" sx={[isFetchingPrice && { color: 'text.disabled' }]}>
                {getFormattedPrice(parseFloat(prices.pack_price.display))}
              </Typography>
            </Stack>
          </Grid>

          <Divider sx={{ mt: 1 }} />
        </Box>

        {/* Quantity and input*/}
        <Stack direction="row" alignItems="center" my={1.5} ml={1}>
          <Typography variant="body2" mr={1}>
            Packs
          </Typography>
          <TextField
            type="number"
            size="small"
            value={quantity}
            onChange={(e) => handleNewQuantity(e)}
            inputProps={{ min: 1, max: 5000 }}
            sx={{ maxWidth: 100 }}
          />
        </Stack>
        <Stack direction="row" alignItems="center" justifyContent="space-between" mx={1}>
          <Typography variant="body2" style={{ fontWeight: 500 }} sx={[isFetchingPrice && { color: 'text.disabled' }]}>
            Estimated Price:
          </Typography>
          <Stack direction="row" alignItems="center">
            <CircularProgress show={isFetchingPrice} size={12} containerProps={{ mr: 1 }} />
            <Typography
              variant="body2"
              style={{ fontWeight: 500 }}
              sx={[isFetchingPrice && { color: 'text.disabled' }]}
            >
              {getFormattedPrice((prevQuantity * prices.pack_price.amount) / 100)}
            </Typography>
          </Stack>
        </Stack>
        <Typography variant="caption" ml={1} color="#9E9E9E" mt={1}>
          Price based on 1 color and 1 decoration location.
        </Typography>
      </Stack>
    </Box>
  );
}

function PackName() {
  const fetcher = useFetcher();
  const { name: initialName } = useLoaderData<typeof loader>();

  const inputRef = React.useRef<HTMLInputElement>(null);
  const [readOnly, setReadOnly] = React.useState(true);

  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    if (
      (e.relatedTarget?.id !== 'clear-button' && !inputRef.current?.value && !initialName) ||
      initialName === inputRef.current?.value
    ) {
      setReadOnly(true);
    }
    if (e.relatedTarget?.id === 'clear-button') {
      inputRef.current?.focus();
    }
  };

  const handleEdit = () => {
    setReadOnly(false);
    inputRef.current?.focus();
  };

  const handleSubmit = (e: React.FormEvent) => {
    if (initialName === inputRef.current?.value) {
      e.preventDefault();
    }
    setReadOnly(true);
    inputRef.current?.blur();
  };

  return (
    <Box my={3}>
      <fetcher.Form method="post" onSubmit={handleSubmit}>
        <Input
          key={initialName}
          name="name"
          defaultValue={initialName}
          placeholder="Name your pack"
          required
          inputRef={inputRef}
          disableUnderline={readOnly}
          onBlur={handleBlur}
          onClick={handleEdit}
          endAdornment={
            <InputAdornment position="end">
              {readOnly ? (
                // see https://www.reddit.com/r/reactjs/comments/borw5f/conditionally_rendering_form_buttons_typebutton/
                <IconButton key="button" type="button" aria-label="edit product name" onClick={handleEdit}>
                  <EditOutlinedIcon fontSize="small" color="action" />
                </IconButton>
              ) : (
                <>
                  <IconButton
                    id="submit-button"
                    key="submit"
                    type="submit"
                    name="_action"
                    value={packActions.changeName}
                    aria-label="update pack name"
                  >
                    <CheckOutlinedIcon fontSize="small" color="action" />
                  </IconButton>
                  <IconButton id="clear-button" type="reset" aria-label="cancel pack name update">
                    <CancelOutlinedIcon fontSize="small" color="action" />
                  </IconButton>
                </>
              )}
            </InputAdornment>
          }
          sx={{
            width: '100%',
            input: {
              fontSize: (theme) => theme.typography.h6.fontSize,
              fontWeight: (theme) => theme.typography.h6.fontWeight,
              ...ellipsisStyle,
              '&:not(:focus)::placeholder': {
                opacity: 1,
              },
            },
          }}
        />
      </fetcher.Form>
    </Box>
  );
}

const filterFetchers =
  (action: PackAction, exist = false) =>
  (f: Fetcher) => {
    const formData = f.submission?.formData;
    const form = new Map(f.submission?.formData.entries());
    const product = getProductFromFormData(formData);
    return form.get('_action') === action && (exist ? product?.id : !product);
  };

export function PackPackaging({ pack }: { pack: Pack }) {
  const fetchers = useFetchers();
  const isFetchingPrice = useIsFetchingPrices();
  const removingContainer = fetchers.some(filterFetchers(packActions.setContainer));
  const addedContainer = getProductFromFormData(
    fetchers.filter(filterFetchers(packActions.setContainer, true))?.at(-1)?.submission?.formData
  );

  const removingFiller = fetchers.some(filterFetchers(packActions.setFiller));
  const addedFiller = getProductFromFormData(
    fetchers.filter(filterFetchers(packActions.setFiller, true))?.at(-1)?.submission?.formData
  );

  const container = addedContainer ?? pack.container;
  const filler = addedFiller ?? pack.filler;

  const showContainerPlacehoder = !container || removingContainer;
  const showFillerPlacehoder = (!filler || removingFiller) && showContainerPlacehoder;

  return (
    <Stack mb={2}>
      <Grid container justifyContent="space-between" alignItems="center" mb={1}>
        <Typography variant="subtitle1">Packaging</Typography>
      </Grid>
      {container && (
        <>
          <ProductInPack
            product={container}
            price={pack.prices.products[container.id] ?? { amount: 0, display: '0' }}
            action={packActions.setContainer}
            disabled={Boolean(addedContainer)}
            pack={pack}
          >
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Typography variant="body2">Container</Typography>
              <Stack direction="row" alignItems="center" spacing={1}>
                <CircularProgress show={isFetchingPrice} size={12} />
                <Typography variant="body2" sx={[isFetchingPrice && { color: 'text.disabled' }]}>
                  {container?.id &&
                    pack.prices.products[container.id] &&
                    getFormattedPrice(pack.prices.products[container.id]?.amount / 100)}
                </Typography>
              </Stack>
            </Stack>
          </ProductInPack>
        </>
      )}
      {filler && (
        <ProductInPack
          product={filler}
          price={pack.prices.products[filler.id] ?? { amount: 0, display: '0' }}
          action={packActions.setFiller}
          disabled={Boolean(addedFiller)}
          pack={pack}
        >
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Typography variant="body2">Filler</Typography>
            <Stack direction="row" alignItems="center" spacing={1}>
              <CircularProgress show={isFetchingPrice} size={12} />
              <Typography variant="body2" sx={[isFetchingPrice && { color: 'text.disabled' }]}>
                {filler?.id &&
                  pack.prices.products[filler.id] &&
                  getFormattedPrice(pack.prices.products[filler.id]?.amount / 100)}
              </Typography>
            </Stack>
          </Stack>
        </ProductInPack>
      )}
      {showContainerPlacehoder && showFillerPlacehoder && <ProductPlaceholder />}
    </Stack>
  );
}

export function PackProducts({ pack }: { pack: Pack }) {
  const fetchers = useFetchers();
  const removing = fetchers.filter((f) => f.submission?.formData.get('_action') === packActions.removeProduct);
  const adding = fetchers
    .filter((f) => f.submission?.formData.get('_action') === packActions.addProduct)
    .map((f) => {
      const product = getProductFromFormData(f.submission?.formData);
      return product && pack.products.some((p) => p.id === product.id) ? null : product;
    })
    .filter(Boolean);

  const isEmpty = pack.products.length + adding.length - removing.length < 1;
  return (
    <Stack>
      <Grid container justifyContent="space-between" alignItems="center" mb={1}>
        <Typography variant="subtitle1">Items</Typography>
      </Grid>
      {isEmpty && <ProductPlaceholder />}
      {pack.products.map((p) => (
        <ProductInPack
          key={p!.id}
          product={p!}
          price={pack.prices.products[p.id.toString()] ?? { amount: 0, display: '0' }}
          action={packActions.removeProduct}
          pack={pack}
        />
      ))}
      {adding.map((p) => (
        <ProductInPack
          key={p!.id}
          product={p!}
          price={{ amount: 0, display: '0' }}
          action={packActions.removeProduct}
          disabled
          pack={pack}
        />
      ))}
    </Stack>
  );
}

function ProductPlaceholder() {
  return (
    <Box p={1} mb={1} display="flex" alignItems="center" bgcolor="#F9F9FA" borderRadius={2} position="relative">
      <Box display="inline-flex" mr={1.25} p={0.5} borderRadius={1} bgcolor="#fff">
        <Image src={`${baseImgUrl}image-placeholder-blue.svg`} alt="image placeholder" width={40} height={40} />
      </Box>
      <Box flex={1} display="flex" flexDirection="column" justifyContent="center">
        <Skeleton
          variant="rounded"
          width={137}
          height={10}
          animation={false}
          sx={{ mb: 1.75, bgcolor: 'rgba(0, 157, 200, 0.12)' }}
        />
        <Skeleton
          variant="rounded"
          width={110}
          height={10}
          animation={false}
          sx={{ mb: 1.75, bgcolor: 'rgba(0, 157, 200, 0.12)' }}
        />
        <Skeleton
          variant="rounded"
          width={80}
          height={10}
          animation={false}
          sx={{ bgcolor: 'rgba(0, 157, 200, 0.12)' }}
        />
      </Box>
    </Box>
  );
}

const StyledSelect = styled(Select)({
  lineHeight: '1.25rem',
  '& .MuiSelect-select': { padding: '0 20px 0 8px !important', fontSize: 12 },
  '& .MuiSelect-icon': { right: 0, fontSize: 20 },
  '& input': { padding: 0, margin: 0, border: 'none' },
});

function ProductInPack({
  product,
  price,
  action,
  disabled,
  children,
  pack,
}: {
  product: Product;
  price: PriceEntry;
  action?: PackAction;
  disabled?: boolean;
  children?: React.ReactNode;
  pack: Pack;
}) {
  const deleteFetcher = useFetcher();
  const changeQuantityFetcher = useFetcher();
  const isFetchingPrice = useIsFetchingPrices();
  const [productInModal, setProductInModal] = React.useState<Product | null>(null);
  const isPackaging = pack.container?.id === product.id || pack.filler?.id === product.id;
  const handleChange = (e: SelectChangeEvent<unknown>) => {
    changeQuantityFetcher.submit(
      {
        _action: packActions.changeUnitsPerPack,
        productId: product.id.toString(),
        unitsPerPack: e.target.value as string,
      },
      { method: 'post' }
    );
  };
  const isDeleting = deleteFetcher.state !== 'idle';

  function handleImageClick() {
    setProductInModal(product);
  }

  function handleCloseModal() {
    setProductInModal(null);
  }

  function itemAlreadyInPack() {
    return (
      pack.products.find((productInPack) => productInPack.id === product.id) ||
      pack.container?.id === product.id ||
      pack.filler?.id === product.id
    );
  }

  return isDeleting ? null : (
    <Box p={1} mb={1} display="flex" alignItems="center" bgcolor="#F9F9FA" borderRadius={2} position="relative">
      <ProductModal product={productInModal} pack={pack} isPackaging={isPackaging} onClose={handleCloseModal} />
      <Box
        display="inline-flex"
        mr={1.25}
        p={0.5}
        borderRadius={1}
        bgcolor="#fff"
        onClick={handleImageClick}
        sx={{ cursor: 'pointer' }}
      >
        {!itemAlreadyInPack() ? (
          //  Product is in Pack
          <Stack direction="row" width={42} height={42} alignItems="center" justifyContent="center">
            <CircularProgress show={true} size={12} />
          </Stack>
        ) : (
          <Image
            src={product.image || product.blank_image || NoImageAvailable}
            responsive={[{ size: { width: 112, height: 112 } }]}
            options={{ fit: 'inside' }}
            width={42}
            height={42}
            style={{
              width: 42,
              height: 42,
              minWidth: 42,
              minHeight: 42,
            }}
          />
        )}
      </Box>
      <Box flex={1}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1.5}>
          <Typography
            variant="body2"
            fontWeight={500}
            lineHeight="22px"
            onClick={handleImageClick}
            sx={{
              ...ellipsisStyle,
              maxWidth: { xs: 180 },
              '&:hover': {
                cursor: 'pointer',
              },
            }}
          >
            {product.name}
          </Typography>

          <deleteFetcher.Form method="post">
            <input type="hidden" name="productId" value={product.id} />
            <IconButton
              type="submit"
              name="_action"
              value={action}
              disabled={disabled}
              sx={{ mr: -0.5, width: '20px', height: '20px' }}
            >
              <DeleteOutlinedIcon fontSize="small" />
            </IconButton>
          </deleteFetcher.Form>
        </Stack>
        {children ?? (
          <Stack direction="row" justifyContent="space-between" my={0.25} alignItems="center">
            <Stack direction="row" alignItems="center">
              <Typography variant="body2" mr={1} onClick={handleImageClick} sx={{ '&:hover': { cursor: 'pointer' } }}>
                Qty per pack:
              </Typography>
              <StyledSelect
                value={pack.items_units_per_pack[product.id] ?? 1}
                onChange={handleChange}
                disabled={disabled}
              >
                {getEmptyArray(10).map((value) => (
                  <MenuItem key={value} value={value}>
                    {value}
                  </MenuItem>
                ))}
              </StyledSelect>
            </Stack>
            <Stack direction="row" alignItems="center" spacing={1}>
              <CircularProgress show={isFetchingPrice} size={12} />
              <Typography variant="body2" sx={[isFetchingPrice && { color: 'text.disabled' }]}>
                {getFormattedPrice(price.amount / 100)}
              </Typography>
            </Stack>
          </Stack>
        )}
      </Box>
    </Box>
  );
}

export function useIsFetchingPrices() {
  return useFetchers().some(
    (f) =>
      f.state !== 'idle' &&
      Object.values(packActions)
        .filter((pa) => pa !== packActions.changeName)
        .includes(f.submission?.formData.get('_action')?.toString() as PackAction)
  );
}

function getProductFromFormData(form?: FormData) {
  try {
    return JSON.parse(form?.get('product')?.toString() ?? '') as Product;
  } catch {
    return null;
  }
}

export function getPricesRequestData(pack: Pack) {
  const { container, filler, products, no_colors } = pack;

  const pricesRequest = {
    pack_quantity: pack.quantity,
    calculate_breakdown: true,
    products: products.map((p) => {
      const color = pack?.items_attributes[p.id]?.attributes?.find((attribute) => attribute.name === 'color')?.value;
      const size = pack?.items_attributes[p.id]?.attributes?.find((attribute) => attribute.name === 'size')?.value;
      const book_type = pack?.items_attributes[p.id]?.attributes?.find(
        (attribute) => attribute.name === 'book_type'
      )?.value;

      let attributes;

      if (book_type) {
        attributes = { color, book_type };
      }
      if (size) {
        attributes = { color, size };
      } else {
        attributes = validCombination(
          p,
          pack?.items_attributes[p.id]?.attributes?.find((attribute) => attribute.name === 'color')?.value
        );
      }
      return {
        product: p.id,
        units_per_pack: pack.items_units_per_pack[p.id],
        attributes,
        ...(no_colors !== undefined && { no_colors }),
      };
    }),
  };

  if (container) {
    pricesRequest.products.push({
      product: container.id,
      units_per_pack: 1,
      attributes: {
        color: pack?.items_attributes[container.id]?.attributes?.find((attribute) => attribute.name === 'color')?.value,
      },
    });
  }
  if (filler) {
    pricesRequest.products.push({
      product: filler.id,
      units_per_pack: 1,
      attributes: {
        color: pack?.items_attributes[filler.id]?.attributes?.find((attribute) => attribute.name === 'color')?.value,
      },
    });
  }
  return pricesRequest;
}

export function parsePrices(pricesResponse: PackBuilderPricesResponse, prevPrices: Pack['prices']) {
  return {
    pack_price: pricesResponse.pack_price,
    products: Object.fromEntries(pricesResponse.products.map((p) => [p.product, p.price])),
    pack_prices: pricesResponse.pack_prices,
    breakdowns: pricesResponse.breakdowns
      ? Object.fromEntries(
          Object.entries(pricesResponse.breakdowns).map(([key, value]) => {
            return [
              key,
              {
                pack_price: value.pack_price,
                products: Object.fromEntries(value.products.map((p) => [p.product, p.price])),
              },
            ];
          })
        )
      : prevPrices.breakdowns,
  };
}
export function parsePricesTotal(pricesResponse: PackBuilderPricesResponse, prevPrices: Pack['prices']) {
  return {
    pack_price: pricesResponse.pack_price,
    pack_prices: pricesResponse.pack_prices,
    products: Object.fromEntries(pricesResponse.products.map((p) => [p.product, p.price])),
    breakdowns: pricesResponse.breakdowns
      ? Object.fromEntries(
          Object.entries(pricesResponse.breakdowns).map(([key, value]) => {
            return [
              key,
              {
                pack_price: value.pack_price,
                products: Object.fromEntries(value.products.map((p) => [p.product, p.price])),
              },
            ];
          })
        )
      : prevPrices.breakdowns,
  };
}

async function getCookieHeader(session: Session) {
  return { headers: { 'Set-Cookie': await commitSession(session) } };
}

async function jsonWithCookieHeader<T>(data: T, session: Session) {
  session.set('packBuilder', data);
  return json(data, await getCookieHeader(session));
}
